import { Empty, Typography } from 'antd';
import styles from './RecurringTransactionView.module.css';
import { formatToINR } from '../../../Utils/utils';
import { ReactComponent as Warning } from '../../../Assets/warning-circle.svg';
import { ReactComponent as NoTxn } from '../../../Assets/noTxn.svg';
import Transaction from '../../Dashboard/Common/Transaction';
import { useContext } from 'react';
import { AppContext } from '../../../App';

const { Text } = Typography;

type Props = {
  isFilterOptions: boolean;
  allTransactions: any[];
  completedTxn: any[];
  pendingTxn: any[];
  categoryBreakdown: any[];
  totalPendingAmount: number;
  totalPaidAmount: number;
};

const RecurringTransactionView = ({
  isFilterOptions,
  allTransactions,
  completedTxn,
  pendingTxn,
  categoryBreakdown,
  totalPendingAmount,
  totalPaidAmount,
}: Props) => {
  const { isAmountVisible }: any = useContext(AppContext);
  if (allTransactions.length === 0) {
    return (
      <Empty
        className={styles.emptyState}
        image={<NoTxn />}
        description={
          <Text className={styles.emptyText}>No transaction found</Text>
        }
      />
    );
  }

  if (!isFilterOptions) {
    return (
      <>
        {allTransactions.map((txn, i) => (
          <Transaction
            key={txn.id ?? i}
            showTransactionDetails
            transaction={{
              ...txn,
              id: i,
              format: true,
            }}
            showDateOfTransaction
          />
        ))}
      </>
    );
  }

  return (
    <>
      <div className={styles.summarySection}>
        <div
          className={`${styles.summaryCard} ${
            completedTxn.length === 0 || pendingTxn.length === 0
              ? ''
              : styles.borderBottom
          }`}
        >
          <div className={styles.amountBlock}>
            <Text className={styles.summaryLabel}>Pending</Text>
            <Text className={styles.topAmountSection}>
              {formatToINR(totalPendingAmount.toString(), isAmountVisible)}
            </Text>
          </div>

          <div className={styles.amountBlockRight}>
            <Text className={styles.summaryLabel}>Paid</Text>
            <Text className={styles.topAmountSection}>
              {formatToINR(totalPaidAmount.toString(), isAmountVisible)}
            </Text>
          </div>
        </div>

        {(completedTxn.length === 0 || pendingTxn.length === 0) && (
          <div className={styles.warningBanner}>
            <Warning />

            <Text className={styles.warningText}>
              {completedTxn.length === 0
                ? 'We could not detect any recurring transactions paid this month.'
                : 'No pending payments remain for this month.'}
            </Text>
          </div>
        )}
      </div>

      {categoryBreakdown.length > 0 && (
        <div className={styles.categorySection}>
          <Text className={styles.sectionTitle}>CATEGORY BREAKDOWN</Text>

          {categoryBreakdown.map((cat, index) => (
            <div key={index} className={styles.categoryCard}>
              <div className={styles.categoryInfo}>
                <Text className={styles.categoryName}>{cat.category}</Text>

                <Text className={styles.categoryCount}>
                  {cat.count} payment{cat.count !== 1 ? 's' : ''}
                </Text>
              </div>

              <Text className={styles.categoryAmount}>
                {formatToINR(cat.totalAmount.toString(), isAmountVisible)}
              </Text>
            </div>
          ))}
        </div>
      )}

      {completedTxn.length > 0 && (
        <>
          <Text className={styles.transactionSectionTitle}>
            PAID THIS MONTH
          </Text>

          {completedTxn.map((txn, i) => (
            <Transaction
              key={i}
              className={
                completedTxn.length - 1 === i && pendingTxn.length === 0
                  ? styles.transactionCard
                  : ''
              }
              showTransactionDetails
              transaction={{
                ...txn,
                id: i,
                format: true,
              }}
              showDateOfTransaction
              isCompletedRec
            />
          ))}
        </>
      )}

      {pendingTxn.length > 0 && (
        <>
          <Text className={styles.transactionSectionTitle}>
            PENDING PAYMENTS
          </Text>

          {pendingTxn.map((txn, i) => (
            <Transaction
              key={i}
              className={
                pendingTxn.length - 1 === i ? styles.transactionCard : ''
              }
              showTransactionDetails
              transaction={{
                ...txn,
                id: i,
                format: true,
              }}
              showDateOfTransaction
              isPendingRec
            />
          ))}
        </>
      )}
    </>
  );
};

export default RecurringTransactionView;
